import { Box, Typography } from '@mui/material';
import Divider from 'components/divider';
import List, { HeadCell } from 'components/list/list';
import React, { useEffect } from 'react';
import _ from 'underscore';
import ScenarioListRow from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-list/scenario-list-row';
import { useR2ScenarioState } from 'views/bluviu-nutrition/features/scenarios-v2/context/r2-scenario-context';
import { ScenarioListItem } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-list-item.type';

interface Filters {
  generation: string;
  states: string[];
  searchValue: string;
}

type Props = {
  filters: Filters;
};

const ScenarioList: React.FC<Props> = ({ filters }) => {
  const {
    r2ScenariosQuery: { data: scenarios, isLoading },
  } = useR2ScenarioState();

  const [rows, setRows] = React.useState<ScenarioListItem[]>(scenarios ?? []);

  useEffect(() => {
    setRows(
      _.chain(scenarios ?? [])
        .filter((item) => (filters.states.length === 0 ? true : _.contains(filters.states, item.state)))
        .filter((item) => !filters.generation || item.generation?.displayName === filters.generation)
        .filter((item) => item.name.toLowerCase().includes(filters.searchValue.toLowerCase()))
        .sortBy('lastUpdated')
        .reverse()
        .value()
    );
  }, [scenarios, filters]);

  // #region Columns
  const columns: HeadCell<ScenarioListItem>[] = [
    {
      id: 'name',
      label: 'Name',
    },
    {
      id: 'generation.displayName',
      label: 'Generation',
      comparator: (a, b) => {
        const yearA = a.generation?.year ?? 0;
        const yearB = b.generation?.year ?? 0;
        if (yearA !== yearB) return yearA - yearB;
        const seasonA = a.generation?.season ?? '';
        const seasonB = b.generation?.season ?? '';
        return seasonA.localeCompare(seasonB);
      },
    },
    {
      id: 'scenarioStart',
      label: 'Scenario Start',
    },
    {
      id: 'lastUpdated',
      align: 'left',
      label: 'Last Updated',
    },
    {
      id: 'state',
      align: 'center',
      label: 'State',
    },
    {
      id: 'appliedAs',
      align: 'left',
      label: 'Applied As',
      info: 'Shows whether the scenario has, or previously had an active feed program applied to the sites.',
    },
    {
      id: 'actions',
      label: '',
      align: 'right',
    },
  ];

  // #endregion

  return (
    <Box display={'flex'} flexDirection={'column'} justifyContent={'flex-start'} gap={4} alignItems={'flex-start'}>
      <Typography variant="h6" color="white">
        Scenarios
      </Typography>
      <Divider flexItem dark />
      <List
        orderBy="name"
        rows={rows}
        sort={setRows}
        options={columns}
        isLoading={isLoading}
        noResult={{
          show: !rows.length,
          text: 'No Results',
        }}>
        {rows.map((scenario, idx) => (
          <ScenarioListRow key={idx} scenario={scenario} />
        ))}
      </List>
    </Box>
  );
};

export default React.memo(ScenarioList);
